import React, { useEffect, useRef, useState } from 'react'
import LocationPicker from '../../../../components/LocationPicker'
import ServicesCustomSelectBox from '../../../../components/BookingFormComponents/servicesCustomSelectBox/index'
import DynamicForm from '../../../../components/FormElements/DynamicForm'
import Button from '../../../../components/FormElements/Button'
import './style.css'
import * as Yup from 'yup';
import useJobApi from '../../../../config/hooks/api/booking/job/useJobApi'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { setAppLoading, setError, setMessage } from '../../../../store/common/generalSlice'
import { Tooltip } from 'antd'
import { getAllServicesIds } from '../../../../config/utils'
import { useMutation } from 'react-query'
import axiosInstance from '../../../../config/api'
import { fetchProfileData } from '../../../../store/common/settingSlice'
import useVisibilityCheck from '../../../../config/hooks/utility/useVisibilityCheck'
import { calculateCommission } from '../../../../config/utils/calculateCommission'
import { finalPriceAfterExtractingCommission } from '../../../../config/utils/finalPriceExtractingCommission'
import CustomPriceField from '../../../../components/CustomPriceField'

const AddQuote = ({ bookingId, onSuccess, qoutesServices, displayServices }) => {
    const dispatch = useDispatch()

    const { profileData } = useSelector(state => state?.setting);

    const [servicesCustomError, setServicesCustomError] = useState(false);
    const [quotesUpdatedArray, setQuotesUpdatedArray] = useState([]);
    const [isStripeConnected, setIsStripeConnected] = useState(true);
    const [commissionValue, setCommissionValue] = useState(() => {
        if (profileData?.userDetail?.isEnabled) {
            return profileData?.userDetail?.percentage
        }
        else {
            return calculateCommission(displayServices)
        }
    });
    
    const { submitQuoteAsSP } = useJobApi();

    // when a user gets back after connecting a stripe account this is used to call
    // an api for profile detail so wallet info will get added and the text to connect
    // account gets hide.
    useVisibilityCheck();

    useEffect(() => {
        if (qoutesServices?.length > 0) {
            setQuotesUpdatedArray(getAllServicesIds(qoutesServices))
        }
    }, [qoutesServices])

    useEffect(() => {
        if (!profileData?.wallet?.accountId) {
            setIsStripeConnected(false)
        }
        else {
            setIsStripeConnected(true)
        }
    }, [profileData])

    const form = {
        name: 'addSPQuoteForm',
        formFields: [
            {
                name: 'services',
                label: { value: "Services", classes: '' },
                type: 'custom',
                placeholder: '',
                width: { md: 24, sm: 24, xs: 24 },
                // component: <ServicesCustomSelectBox
                //     classes='shadow-md border'
                //     servicesCustomError={servicesCustomError}
                //     setServicesCustomError={setServicesCustomError}
                //     spServicesData={qoutesServices}
                // />,
                component: <SelectedServicesList spServicesData={displayServices} />,
                classes: '',
                icon: null
            },
            {
                name: 'price',
                label: { value: 'Price', classes: '' },
                type: 'custom',
                width: { md: 24, sm: 24, xs: 24 },
                component: <CustomPriceField commissionPercentage={commissionValue} />,
                classes: '',
                icon: null
            },
            {
                name: 'vatIncluded',
                label: { value: '', classes: '' },
                type: 'radio',
                placeholder: '',
                width: { md: 24, sm: 24, xs: 24 },
                options: [
                    { name: 'Price Include VAT', value: 0 },
                    { name: 'Price does not include VAT', value: 1 },
                ],
                defaultValue: 0,
                component: null,
                classes: 'radio-vat flex',
                icon: null,
                design: 'checkbox'
            },
            {
                name: 'location',
                label: { value: 'Location', classes: '' },
                type: 'custom',
                placeholder: '4058 Little York Rd, Houston',
                width: { md: 24, sm: 24, xs: 24 },
                component: <LocationPicker />,
                classes: '',
                icon: null
            },
            {
                name: 'time',
                label: { value: 'Time:', classes: '' },
                type: 'timepicker',
                placeholder: '12:18PM',
                width: { md: 12, sm: 12, xs: 24 },
                component: null,
                classes: 'w-full',
                icon: null
            },
            {
                name: 'date',
                label: { value: 'Date:', classes: '' },
                type: 'datepicker',
                placeholder: '10 June, 2024',
                width: { md: 12, sm: 12, xs: 24 },
                component: null,
                classes: 'w-full',
                icon: null
            },
            {
                name: 'additionalInfo',
                label: { value: 'Any Addtional Info/Note:', classes: '' },
                type: 'textarea',
                placeholder: 'Any Addtional Info/Note',
                width: { md: 24, sm: 24, xs: 24 },
                component: null,
                classes: '',
                icon: null
            },
        ],
        formValidation: {
            // Price field must be a number, non-negative, and required
            price: Yup.number()
                .required('Price is required')
                .min(0, 'Price cannot be negative')
                .typeError('Price must be a number'),
            additionalInfo: Yup.string()
                .max(1000, 'You cannot enter more than 1000 characters'),
            location: Yup.object()
                .shape({
                    lat: Yup.number()
                        .required('Latitude is required')
                        .min(-90, 'Latitude must be greater than or equal to -90')
                        .max(90, 'Latitude must be less than or equal to 90'),
                    lng: Yup.number()
                        .required('Longitude is required')
                        .min(-180, 'Longitude must be greater than or equal to -180')
                        .max(180, 'Longitude must be less than or equal to 180'),
                })
                .required('Location is required') // Ensures the location object exists
                .test('hasKeys', 'Location should not be empty', value =>
                    value && 'lat' in value && 'lng' in value
                ),
            services: Yup.array()
                .of(
                    Yup.mixed()
                        .test('is-valid-id', 'ID must be a number or a string', (value) =>
                            typeof value === 'number' || typeof value === 'string'
                        )
                        .required('ID is required')
                )
                .min(1, 'At least one service should be selected')
        }
    }

    const handleSubmit = async (values) => {
        try {
            const data = { ...values }
            const { lat: latitude, lng: longitude, address } = values?.location
            const date = dayjs(data.date);
            const formattedDate = date.format('YYYY-MM-DD');
            const formattedTime = dayjs(data?.time).format('HH:mm');

            delete data?.location
            // payload.services = [].concat(...Object.values(temp?.services))
            // payload.services = [18,19,20] //dev env

            dispatch(setAppLoading(true))

            const response = await submitQuoteAsSP({
                ...data,
                bookingId,
                date: formattedDate,
                time: formattedTime,
                vatIncluded: data?.vatIncluded === 0 ? true : false,
                // services: data?.services,
                services: quotesUpdatedArray,
                latitude,
                longitude,
                address
            })
            if (response) {
                onSuccess()
                dispatch(setAppLoading(false))
                dispatch(setMessage(response?.data?.message))
            }
        } catch (error) {
            console.log(error)
            dispatch(setAppLoading(false))
        }
    };

    const stripeSetupMutation = useMutation(async () => {
        let res = await axiosInstance.post('/stripe/connected-account', {});
        return res
    },
        {
            onSuccess: (res) => {
                dispatch(setMessage(res?.data?.data?.message))
                window.open(res?.data?.data?.stripeLink?.url, '_blank')
            },
            onError: (error) => {
                dispatch(setError(error.response?.data?.message || 'An error occured'))
            }
        }
    )

    const submitForm = () => {
        document.querySelector('form#addSPQuoteForm')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
    }

    return (
        <div className='p-4 text-center w-full'>
            <h6 className='heading-6 font-bold mb-4'>Submit a Quote</h6>
            <DynamicForm id='addSPQuoteForm' form={form} onSubmit={handleSubmit} />
            <div className="flex justify-center w-full">
                <div className='sm:w-3/4'>
                    <Button.Basic
                        onClick={submitForm}
                        disabled={!isStripeConnected}
                        icon={null}
                        loading={false}
                        type="primary"
                        size='medium'
                        text='Submit'
                        className='btn-primary w-full'
                    />
                </div>
            </div>
            {
                !isStripeConnected && <p className='my-3'>Please complete your Stripe account setup before submitting.
                    {
                        stripeSetupMutation?.isLoading ?
                            ' Loading ...'
                            :
                            <span className='text-red-400 cursor-pointer' onClick={() => stripeSetupMutation.mutate()}> Click here to get started.</span>
                    }
                </p>
            }
        </div>
    )
}

export default AddQuote



const SelectedServicesList = ({ spServicesData }) => {

    const { profileData } = useSelector(state => state?.setting);

    const getServicesList = (services) => {
        return (
            <ul className='m-0 p-0'>
                {services.map((service, index) => (
                    <li key={index}>{index + 1}. {service?.name}</li>
                ))}
            </ul>
        )
    }

    return (
        <>
            <div className='flex flex-wrap items-center shadow-md p-3 rounded-md border hover:border-gray-600 transition'>
                {spServicesData && spServicesData.length ? (
                    <React.Fragment>
                        {spServicesData.slice(0, 3).map((service, index) => (
                            <small key={'requestedService' + index} className='text-gray-600 text-xs font-semibold' >
                                {service?.name}
                                {/* <span className="font-bold">
                                {" £ "+service?.percentage+"%"}
                                </span> */}
                                {index < spServicesData.slice(0, 3).length - 1 &&
                                    (<span className='mr-1'>,</span>)}
                                {index == 2 && spServicesData.length > 3 ?
                                    (<span className='mr-1'>...</span>) : ''}
                            </small>
                        ))}
                        {spServicesData?.length > 3 ? (
                            <Tooltip title={getServicesList(spServicesData)}>
                                <p className='text-xs sm:ml-2 cursor-pointer shrink-0 whitespace-nowrap'>show all</p>
                            </Tooltip>
                        ) : ''}
                    </React.Fragment>

                ) : ''}
            </div>
            <div className='w-full'>
                {/* {
                    profileData?.userDetail?.isEnabled ?
                        <p className='text-start text-red-600'>{profileData?.userDetail?.percentage}% commission will be charged from the service provider.</p>
                        :
                        <p className='text-start text-red-600'>{calculateCommission(spServicesData)}% commission will be charged based on the service’s percentage.</p>
                } */}
            </div>
        </>

    )
}

